import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import { AddIcon, SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Badge,
	Button,
	Container,
	Empty,
	GraphQLErrorMessage,
	Jumbo,
	Pagination,
	Popover,
	Skeleton,
	Table,
	tokens,
} from '@treams/treams-ui';

import AnnouncementModal from './AnnouncementModal';

type AnnouncementFragment = graphql.AnnouncementFragmentFragment;

function AnnouncementRow({
	announcement,
	onEdit,
	onDelete,
}: {
	announcement: AnnouncementFragment;
	onEdit: () => void;
	onDelete: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [confirmDelete, setConfirmDelete] = useState(false);

	const workspaceNames = announcement.filters.workspaces.map((w) => w.id).join(', ');

	return (
		<Table.Row>
			<Table.Cell basis={0.05}>
				<Badge color={announcement.enabled ? 'green' : 'lightBlue'} size={12} />
			</Table.Cell>
			<Table.Cell basis={0.25}>
				<Box style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
					{localize(announcement.title).text || '—'}
				</Box>
			</Table.Cell>
			<Table.Cell basis={0.25}>
				<Box style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
					{workspaceNames || t('announcements.allWorkspaces')}
				</Box>
			</Table.Cell>
			<Table.Cell basis={0.15}>{moment(announcement.createdAt).format('L')}</Table.Cell>
			<Table.Cell basis={0.3}>
				<Flex>
					<Button small type="secondary" title={t('common.edit')} onClick={onEdit} mr={2} />
					<Popover.Confirm
						visible={confirmDelete}
						title={t('announcements.delete.confirm')}
						confirmTitle={t('common.delete')}
						cancelTitle={t('common.cancel')}
						confirmType="destructive"
						onCancel={() => setConfirmDelete(false)}
						onConfirm={() => {
							onDelete();
							setConfirmDelete(false);
						}}
					>
						<Button
							small
							type="destructive"
							title={t('common.delete')}
							onClick={() => setConfirmDelete(true)}
						/>
					</Popover.Confirm>
				</Flex>
			</Table.Cell>
		</Table.Row>
	);
}

function AnnouncementsTable() {
	const { t } = useTranslation();
	const [error, attempt] = useErrorBoundary();

	const [pagination, setPagination] = useState<Pagination>({ first: 10 });
	const [editingAnnouncement, setEditingAnnouncement] = useState<AnnouncementFragment | null>(null);
	const [showCreateModal, setShowCreateModal] = useState(false);

	const query = graphql.useAnnouncementsQuery({
		variables: {
			...pagination,
		},
	});

	const [deleteAnnouncement] = graphql.useDeleteAnnouncementMutation();

	const handleDelete = attempt(async (id: string) => {
		await deleteAnnouncement({
			variables: {
				input: { announcementID: id },
			},
		});
		query.refetch();
	});

	const handleModalDismiss = () => {
		setShowCreateModal(false);
		setEditingAnnouncement(null);
		query.refetch();
	};

	return (
		<>
			<GraphQLErrorMessage error={error} />
			<Flex mb={2} justifyContent="flex-end">
				<Button
					small
					icon={<AddIcon />}
					title={t('announcements.create')}
					onClick={() => setShowCreateModal(true)}
				/>
			</Flex>

			<Table>
				<Table.Header>
					<Table.Cell basis={0.05}>{t('announcements.table.status')}</Table.Cell>
					<Table.Cell basis={0.25}>{t('announcements.table.title')}</Table.Cell>
					<Table.Cell basis={0.25}>{t('announcements.table.workspaces')}</Table.Cell>
					<Table.Cell basis={0.15}>{t('announcements.table.createdAt')}</Table.Cell>
					<Table.Cell basis={0.3}>{t('announcements.table.actions')}</Table.Cell>
				</Table.Header>
				{query.data ? (
					query.data.announcements.edges.length > 0 ? (
						query.data.announcements.edges.map(({ node }) => (
							<AnnouncementRow
								key={node.id}
								announcement={node}
								onEdit={() => setEditingAnnouncement(node)}
								onDelete={() => handleDelete(node.id)}
							/>
						))
					) : (
						<Skeleton.Empty
							contents={
								<Empty
									icon={<SearchIcon />}
									title={t('announcements.empty.title')}
									description={t('announcements.empty.description')}
								/>
							}
						>
							<Skeleton.Repeat count={3}>
								<Table.Row>
									<Table.Cell.Skeleton basis={0.05} />
									<Table.Cell.Skeleton basis={0.25} />
									<Table.Cell.Skeleton basis={0.25} />
									<Table.Cell.Skeleton basis={0.15} />
									<Table.Cell.Skeleton basis={0.3} />
								</Table.Row>
							</Skeleton.Repeat>
						</Skeleton.Empty>
					)
				) : (
					<Skeleton.Repeat count={3}>
						<Table.Row>
							<Table.Cell.Skeleton basis={0.05} />
							<Table.Cell.Skeleton basis={0.25} />
							<Table.Cell.Skeleton basis={0.25} />
							<Table.Cell.Skeleton basis={0.15} />
							<Table.Cell.Skeleton basis={0.3} />
						</Table.Row>
					</Skeleton.Repeat>
				)}
			</Table>
			<Table.Paging pageInfo={query.data?.announcements.pageInfo} onPaginate={setPagination} />

			{/* Create Modal */}
			{showCreateModal && (
				<Popover backdrop center visible>
					<AnnouncementModal onDismiss={handleModalDismiss} />
				</Popover>
			)}

			{/* Edit Modal */}
			{editingAnnouncement && (
				<Popover backdrop center visible>
					<AnnouncementModal announcement={editingAnnouncement} onDismiss={handleModalDismiss} />
				</Popover>
			)}
		</>
	);
}

export default function AnnouncementsSettings() {
	const { t } = useTranslation();

	return (
		<>
			<Jumbo title={t('announcements.title')} description={t('announcements.description')} />
			<Container mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
				<AnnouncementsTable />
			</Container>
		</>
	);
}
